import React, { useEffect, useState, useRef } from "react";

// Modules
import { useSelector, useDispatch } from "react-redux";

// App
import { serverUrl, counterEnabled } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import CompanyappLoader from "../../partials/companyappLoader";
import PresentationFilters from "./partials/filters";
import NoData from "../../partials/noData";
import Presentation from "./partials/presentation/view/presentation";
import Pagination from "../../partials/pagination";
import {
  getFavoritesByContentType,
  favoritesContentType,
} from "../../utils/helpers";
import { patchNode } from "../../core/postNode";

// UI components
import { Row, Col } from "react-bootstrap";
import { Box, Alert } from "@mui/material";

const pageSize = 24;
const contentType = favoritesContentType("presentation");

export default function Presentations() {
  // redux
  const user = useSelector((state) => state).authReducer.user;
  const permissions = useSelector((state) => state).permissionsReducer
    .permissions;
  const counter = useSelector((state) => state).counterReducer;

  const [isLoading, setLoading] = useState(true);
  const [isSearching, setSearching] = useState(false);
  const [isError, setError] = useState(false);
  const [errorStatus, setErrorStatus] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState({
    title: "",
    category: 0,
  });
  const [categories, setCategories] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const [presentations, setPresentations] = useState([]);

  const favorites = useSelector((state) => state).favoritesReducer;
  const favStrings = getFavoritesByContentType(favorites, contentType, true);

  // hooks
  const dispatch = useDispatch();
  const didFetch = useRef(false);

  useEffect(() => {
    if (counterEnabled) {
      updateRemoteCounter();
    }
    // eslint-disable-next-line
  }, []);

  // initial render
  useEffect(() => {
    getCategories();
  }, []);

  useEffect(() => {
    getContent();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters, activeIndex]);

  const getCategories = () => {
    getOnlineNode("api/categories?vid=presentation_categories&used=1")
      .then((response) => {
        let newCategories = [
          {
            value: 0,
            label: "All Categories",
            has_content: true,
            has_view_permission: true,
          },
        ];
        response.data.forEach((item) => {
          if (item.has_view_permission === true) {
            if (item.vid === "presentation_categories") {
              newCategories.push({
                ...item,
                value: item.tid,
                label: item.title,
              });
            }
          }
        });

        setCategories(newCategories);
      })
      .catch((error) => {
        // failed to get taxonomies
        // console.log("Failed to get taxonomies: ", error);
      });
  };

  useEffect(() => {
    if (didFetch.current) {
      setCurrentPage(1);
      setLoading(true);
    }

    didFetch.current = true;

    // // eslint-disable-next-line
  }, [activeIndex, favStrings]);


  const getContent = () => {
    setSearching(true);

    let path = `api/user_presentations?status=true&slide_status=true&live=true&simplified=true&items_per_page=${pageSize}&page=${
      currentPage ? currentPage - 1 : 0
    }`;

    if (filters.category && filters.category !== 0) {
      path = path + "&category=" + filters.category;
    }

    if (filters.title && filters.title !== "") {
      path = path + "&id_or_title=" + filters.title;
    }

    if (presentations.length > 0) {
      const lastElement = presentations[presentations.length - 1];

      if (lastElement && lastElement.id) {
        path += `&lastId=${lastElement.id}`;
      }
    }

    if (activeIndex === 1 && favStrings !== "") {
      path += `&id=${favStrings}`;
    }

    getOnlineNode(path)
      .then((response) => {
        setLoading(false);
        setSearching(false);
        setPresentations(response.data.rows);
      })
      .catch((error) => {
        setError(true);

        if (error?.response?.status) {
          setErrorStatus(error.response.status);
        }

        if (error?.response?.statusText) {
          setErrorMessage(error.response.statusText);
        } else {
          setErrorMessage("Failed to fetch presentations. Unknown error!");
        }
      });
  };

  const updateRemoteCounter = () => {
    const updateData = {
      contentType: "presentation",
      acknowledgedCount: counter?.contentCounts?.presentation || 0,
    };

    patchNode(`${serverUrl}/api/v2/counter`, updateData, user.csrf_token)
      .then((response) => {
        if (response.data) {
          dispatch({ type: "UPDATE_COUNTER", payload: response.data });
        }
      })
      .catch((error) => {
        // console.log('error: ', error);
      });
  };

  return (
    <>
      {isLoading ? (
        <div className="circular-container fs">
          <CompanyappLoader />
        </div>
      ) : (
        <>
          {isError ? (
            <Row className="h-100 justify-content-center align-self-center">
              <Col>
                <Box
                  className="d-flex h-100 justify-content-center align-items-center align-content-center"
                  sx={{ flexDirection: "column" }}
                >
                  <Alert variant="filled" severity="error">
                    {errorStatus ? `${errorStatus} - ` : ""}
                    {errorMessage}
                  </Alert>
                </Box>
              </Col>
            </Row>
          ) : (
            <main className="presentations viewer screen">
              <PresentationFilters
                pageTitle="Presentations"
                toggleSwitch={
                  permissions?.access_presentation_creator_mode === true
                    ? true
                    : false
                }
                switchLink={"/presentations-creator"}
                switchStatus={false}
                categories={categories}
                createPresentation={false}
                data={presentations}
                user={user}
                onApply={(filters) => {
                  setFilters(filters);
                }}
                onUpdate={() => {
                  setCurrentPage(1);
                }}
                activeTabIndex={activeIndex}
                getFavourites={() => {
                  setActiveIndex(activeIndex === 0 ? 1 : 0);
                  setSearching(true);
                }}
              />

              {isSearching ? (
                <div className="circular-container fs">
                  <CompanyappLoader />
                </div>
              ) : (
                <>
                  {presentations.length > 0 ? (
                    <>
                      <Row className="presentation-items-container">
                        {presentations.map((item, index) => (
                          <Presentation
                            key={`presentation-${item.id}-index-${index}`}
                            item={item}
                            user={user}
                            contentType={contentType}
                          />
                        ))}
                      </Row>
                    </>
                  ) : (
                    <NoData activeIndex={0} />
                  )}
                  <Pagination
                    owner="admin"
                    containerClasses={`creator-pagination d-flex justify-content-center align-items-center mt-4`}
                    previousDisabled={currentPage === 1 ? true : false}
                    nextDisabled={
                      presentations.length < pageSize ? true : false
                    }
                    currentPage={currentPage}
                    onPrevious={() => {
                      const current = currentPage <= 1 ? 1 : currentPage - 1;

                      setCurrentPage(current);
                    }}
                    onNext={() => {
                      const current = currentPage + 1;
                      setCurrentPage(current);
                    }}
                  />
                </>
              )}
            </main>
          )}
        </>
      )}
    </>
  );
}
